/**
 * Small, dependency-free retry wrapper around `fetch`.
 *
 * Why not React Query? Our client surface is tiny (two one-shot requests
 * — `/api/playlists` and `/api/analyze`) and TMM polling already has its
 * own state machine. A 100-line helper gives us 90% of the benefit
 * (transient-failure resilience) without dragging in a new dependency
 * or a global provider.
 *
 * Defaults — retry up to 3 times (so 4 total attempts) on transient
 * failures only:
 *   - Network errors / `TypeError` thrown by `fetch`
 *   - HTTP 408, 429, 500, 502, 503, 504
 * Never retries:
 *   - Other 4xx (auth, validation, …) — those are bugs, not transients
 *   - Aborted requests (caller cancelled)
 *
 * Backoff: exponential with full jitter, capped at `maxDelayMs`. When
 * the upstream sends `Retry-After` (seconds or HTTP-date), we honour it
 * verbatim instead of computing a backoff.
 */

export type FetchInput = Parameters<typeof fetch>[0];
export type FetchInit = Parameters<typeof fetch>[1];

export type FetchWithRetryOptions = {
	retries?: number;
	baseDelayMs?: number;
	maxDelayMs?: number;
	shouldRetry?: (params: {
		attempt: number;
		response?: Response;
		error?: unknown;
	}) => boolean;
	fetchImpl?: typeof fetch;
};

const DEFAULT_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 300;
const DEFAULT_MAX_DELAY_MS = 2000;
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

const isAbortError = (err: unknown): boolean =>
	typeof err === 'object' &&
	err !== null &&
	(err as {name?: string}).name === 'AbortError';

const defaultShouldRetry = ({
	response,
	error,
}: {
	response?: Response;
	error?: unknown;
}): boolean => {
	if (error) return !isAbortError(error);
	if (response) return RETRYABLE_STATUSES.has(response.status);
	return false;
};

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(new DOMException('Aborted', 'AbortError'));
			return;
		}
		const id = setTimeout(resolve, ms);
		signal?.addEventListener(
			'abort',
			() => {
				clearTimeout(id);
				reject(new DOMException('Aborted', 'AbortError'));
			},
			{once: true},
		);
	});

function parseRetryAfter(header: string | null): number | null {
	if (!header) return null;
	const trimmed = header.trim();
	// RFC 9110 delta-seconds is a non-negative decimal integer. Refuse
	// scientific notation (`1e6`), hex (`0x10`), whitespace-only, etc.
	if (/^\d+$/.test(trimmed)) {
		const seconds = Number(trimmed);
		if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
	}
	const date = Date.parse(trimmed);
	if (Number.isFinite(date)) {
		const delta = date - Date.now();
		return delta > 0 ? delta : 0;
	}
	return null;
}

function backoffDelay(attempt: number, base: number, max: number): number {
	// Exponential with full jitter — see AWS architecture blog. attempt
	// is 0-indexed (0 = first retry).
	const exp = Math.min(max, base * 2 ** attempt);
	return Math.floor(Math.random() * exp);
}

export async function fetchWithRetry(
	input: FetchInput,
	init?: FetchInit,
	options: FetchWithRetryOptions = {},
): Promise<Response> {
	const {
		retries = DEFAULT_RETRIES,
		baseDelayMs = DEFAULT_BASE_DELAY_MS,
		maxDelayMs = DEFAULT_MAX_DELAY_MS,
		shouldRetry = defaultShouldRetry,
		fetchImpl = fetch,
	} = options;

	const signal = init?.signal ?? undefined;

	let attempt = 0;
	while (true) {
		let response: Response | undefined;
		let error: unknown;
		try {
			response = await fetchImpl(input, init);
		} catch (err) {
			error = err;
		}

		const exhausted = attempt >= retries;
		const shouldTry = !exhausted && shouldRetry({attempt, response, error});

		if (!shouldTry) {
			if (response) return response;
			throw error;
		}

		const retryAfterMs = response
			? parseRetryAfter(response.headers.get('retry-after'))
			: null;
		// Cap any honoured `Retry-After` at maxDelayMs — protects against
		// upstreams (or hostile intermediaries) sending huge values that
		// would pin the browser tab in setTimeout for hours.
		const delay =
			retryAfterMs != null
				? Math.min(retryAfterMs, maxDelayMs)
				: backoffDelay(attempt, baseDelayMs, maxDelayMs);
		await wait(delay, signal);
		attempt++;
	}
}
